<template>
  <div class="tw-flex tw-h-full tw-flex-col tw-bg-white">
    <t-auth-header />
    <section
      class="tw-mb-auto tw-flex tw-flex-col tw-items-center tw-self-center tw-p-4"
    >
      <cl-heading class="tw-text-center">
        {{ $t("set-new-password") }}
      </cl-heading>
      <form class="tw-w-full tw-max-w-md" @submit.prevent>
        <cl-form-group class="tw-mb-6 tw-mt-4">
          <cl-form-input
            :id="'email'"
            :name="'email'"
            v-model:value="formData.email"
            :placeholder="$t('email-address')"
            :size="'large'"
            :state="!v$.email.$dirty ? null : !v$.email.$error"
          />
          <span
            v-if="v$.email.$error"
            class="tw-text-base tw-font-normal tw-text-system-error-500"
            >{{ $t("valid-email-format") }}</span
          >
        </cl-form-group>
        <cl-form-group class="tw-mb-6">
          <t-password-input
            :id="'password'"
            v-model:value="formData.password"
            :name="'password'"
            :placeholder="t('password-placeholder')"
            :data-test-id="`set-password-input-password`"
            :state="!v$.password.$dirty ? null : !v$.password.$error"
            :size="'large'"
            @input="v$.password.$reset()"
            @blur="handleBlur('password')"
          />
          <span v-if="v$.password.$error" class="tw-text-system-error-500">{{
            $t("password-requirements")
          }}</span>
        </cl-form-group>
        <cl-form-group class="tw-mb-6">
          <t-password-input
            :id="'confirm-password'"
            v-model:value="formData.confirm_password"
            :name="'confirm-password'"
            :placeholder="t('confirm-password-placeholder')"
            :data-test-id="`set-password-input-password-confirm`"
            :state="
              !v$.confirm_password.$dirty ? null : !v$.confirm_password.$error
            "
            :size="'large'"
            @input="v$.confirm_password.$reset()"
            @blur="handleBlur('confirm_password')"
          />
          <span
            v-if="v$.confirm_password.$error"
            class="tw-text-system-error-500"
            >{{ $t("confirm-password-validation") }}</span
          >
        </cl-form-group>
        <cl-form-group class="tw-mb-6 tw-items-center">
          <div class="tw-w-[70%]">
            <cl-button
              :variant="'secondary'"
              :size="'large'"
              :type="'submit'"
              :disabled="fetching"
              fullwidth
              data-test-id="password-reset-continue-btn"
              @on-click="submit"
            >
              <cl-spinner
                :loading-text="$t('loading')"
                v-if="fetching"
                :size="'small'"
                class="tw-mr-2"
              />
              <span>{{ $t("continue") }}</span>
            </cl-button>
          </div>
        </cl-form-group>
        <cl-form-group class="tw-mb-6">
          <cl-p class="tw-text-center">
            {{ $t("sign-in-return-to") }}
            <router-link
              v-slot="{ href, navigate }"
              :to="{ name: 'auth-sign-in' }"
            >
              <a
                :href="href"
                class="tw-text-primary-grey-700"
                @click="navigate"
                >{{ $t("sign-in-text-block") }}</a
              >
            </router-link>
          </cl-p>
        </cl-form-group>
      </form>
    </section>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed } from "vue";
import { useVuelidate } from "@vuelidate/core";
import { email, required, sameAs } from "@vuelidate/validators";
import { useRouter, useRoute } from "vue-router";
import { useI18n } from "vue-i18n";

import TAuthHeader from "@/components/TAuthHeader/index.vue";
import { STRONG_PASSWORD } from "@/constants";
import { useToastStore } from "@/components/TToasts/toast.store";
import { usePasswordResetStore } from "@/store/passwordReset.store";
import TPasswordInput from "@/components/TPasswordInput";

type FormData = {
  email: string;
  password: string;
  confirm_password: string;
};

type SetNewPasswordPayload = {
  email: string;
  password: string;
  confirm_password: string;
  token: string;
};

type SetNewPasswordResponse = {
  data: {
    message: string;
  };
};

const router = useRouter();
const route = useRoute();
const toastStore = useToastStore();
const passwordResetStore = usePasswordResetStore();
const { t } = useI18n();

const fetching = ref<boolean>(false);
const formData = reactive<FormData>({
  email: "",
  password: "",
  confirm_password: "",
});

const rules = {
  email: { required, email },
  password: {
    required,
    strongPassword: (password: string): boolean =>
      STRONG_PASSWORD.test(password),
  },
  confirm_password: {
    required,
    sameAsPassword: sameAs(computed(() => formData.password)),
  },
};

const v$ = useVuelidate(rules, formData);

const submit = async (): Promise<void> => {
  const isValid = await v$.value.$validate();

  if (!isValid) {
    return;
  }

  try {
    fetching.value = true;

    const payload: SetNewPasswordPayload = {
      email: formData.email,
      password: formData.password,
      confirm_password: formData.confirm_password,
      token: route.query.token as string,
    };

    const res = (await passwordResetStore.setNewPassword(
      payload,
    )) as SetNewPasswordResponse;

    toastStore.displayToast({
      title: t("success"),
      message: res.data.message,
      duration: 2000,
      variant: "success",
      testId: "set-password-toast-success",
    });

    await router.push({ name: "auth-sign-in" });
  } catch {
    // Handled in axios interceptor
  } finally {
    fetching.value = false;
  }
};

const handleBlur = async (field: string) => {
  const isValid = await v$.value[field].$validate();
  if (!isValid) {
    return;
  }
};
</script>
